"use client";

import Link from "next/link";
import { ArrowRight, Building2, Briefcase, Vote, User, Shield, TrendingUp } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { clients as data } from "@/lib/data/about";

const iconMap = { Building2, Briefcase, Vote, User, Shield, TrendingUp } as const;
const accentMap = {
  crimson: "var(--color-crimson)",
  purple: "var(--color-purple)",
  cyan: "var(--color-cyan)",
  emerald: "var(--color-emerald)",
  amber: "var(--color-amber)",
  denim: "var(--color-denim)",
  violet: "var(--color-violet)",
} as const;

export function Clients() {
  return (
    <Section id="clients" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-emerald)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.segments.map((seg, i) => {
            const Icon = (iconMap[Object.keys(iconMap)[i % Object.keys(iconMap).length] as keyof typeof iconMap] ?? Building2);
            const accent = accentMap[seg.accent];
            return (
              <Reveal key={seg.code} delay={i * 60}>
                <div
                  className="glass rounded-2xl p-5 h-full hover:scale-[1.02] transition-transform"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className="grid place-items-center h-10 w-10 rounded-lg"
                      style={{
                        background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-4 w-4" style={{ color: accent }} />
                    </span>
                    <span className="eyebrow-tiny font-bold" style={{ color: accent }}>
                      {seg.code}
                    </span>
                  </div>
                  <h3 className="text-base">{seg.label}</h3>
                  <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{seg.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={400}>
          <div className="mt-10 glass rounded-2xl p-6">
            <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Why They Choose CryptoMize</div>
            <div className="mt-5 grid sm:grid-cols-3 gap-5">
              {data.whyChoose.map((w, i) => (
                <div key={w.title}>
                  <div className="flex items-center gap-2">
                    <span
                      className="grid place-items-center h-6 w-6 rounded-full text-[10px] font-bold"
                      style={{
                        background: "color-mix(in oklch, var(--color-crimson) 18%, transparent)",
                        color: "var(--color-crimson)",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-sm font-bold">{w.title}</h3>
                  </div>
                  <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{w.body}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
